import { FC, useMemo, useState } from 'react';

import DataTable from 'components/tables/data-table';
import { downloadUngroupedCsv, fetchActiveUnitList, fetchActiveUnitsUngrouped } from 'services';
import { downloadCsv } from 'services/download-csv';

import { useAuth } from 'contexts';
import stackDataTableColumns, { defaultStackColumns } from 'pages/units/grid/stack-columns';
import unitDataColumns from 'pages/units/grid/unit-columns';
import { ActiveUnit, UnitRow } from 'types';
import './yard-management.scss';

/* only unit numbers link here; booking and redelivery stay plain text */
const UNIT_LINK_FIELDS: (keyof UnitRow | keyof ActiveUnit)[] = [
  'containerNumber',
  'chassisNumber',
  'gensetNumber',
  'unitNumber'
];

const keepUnitLinksOnly = <T extends { field: keyof UnitRow | keyof ActiveUnit }>(column: T) =>
  UNIT_LINK_FIELDS.includes(column.field) ? column : { ...column, renderCell: undefined };

const GateUnits: FC = () => {
  const [groupByTransaction, setGroupByTransaction] = useState(true);
  const { userProfile } = useAuth();

  const initialColumns = useMemo(() => {
    if (groupByTransaction) {
      return userProfile.columnPreferences.filter(cp => cp.table === 'UNIT_ASSOCIATED').length > 0
        ? userProfile.columnPreferences
            .filter(cp => cp.table === 'UNIT_ASSOCIATED')
            .map(({ column, width }) => {
              return { field: column, width };
            })
        : defaultStackColumns.map(c => {
            return { field: c, width: 80 };
          });
    } else {
      return userProfile.columnPreferences.filter(cp => cp.table === 'UNIT_UNSTACKED').length > 0
        ? userProfile.columnPreferences
            .filter(cp => cp.table === 'UNIT_UNSTACKED')
            .map(({ column, width }) => {
              return { field: column, width };
            })
        : [
            'unitNumber',
            'booking',
            'customer',
            'receivedDate',
            'sizeType',
            'unitStatus',
            'statusReason',
            'licensePlate',
            'loaded',
            'notes',
            'onHold',
            'holdNotes',
            'carbCompliant'
          ].map(c => {
            return { field: c, width: 80 };
          });
    }
  }, [groupByTransaction, userProfile.columnPreferences]);

  return (
    <div className="page" id="gate-units">
      <div className="page-header">
        <h1>YARD MANAGEMENT</h1>
        <div className="d-flex g-3">
          <label className="text-base">GROUP ASSOCIATED</label>
          <label className="switch">
            <input
              type="checkbox"
              checked={groupByTransaction}
              onChange={e => setGroupByTransaction(e.target.checked)}
            />
            <span className="slider round" />
          </label>
        </div>
      </div>
      <div className="flex-fill">
        {groupByTransaction ? (
          <DataTable
            key="grouped"
            fetchData={fetchActiveUnitList}
            fetchCsv={downloadCsv('units')}
            columns={stackDataTableColumns.map(keepUnitLinksOnly)}
            initialColumns={initialColumns}
            filterByLocation
          />
        ) : (
          <DataTable
            key="ungrouped"
            fetchData={fetchActiveUnitsUngrouped({ active: true })}
            fetchCsv={downloadUngroupedCsv({ active: true })}
            columns={unitDataColumns.map(keepUnitLinksOnly)}
            initialColumns={initialColumns}
            filterByLocation
          />
        )}
      </div>
    </div>
  );
};

export default GateUnits;
